/**
 * One Ant Design theme for the whole app. Pages should take colours, radii and
 * spacing from here (or from the CSS variables in styles/admin.css) instead of
 * hard-coding hex values inline, so every table, modal and card looks the same.
 *
 * The primary colour is the indigo of the Vouchain logo.
 */
export const palette = {
  primary: '#5b5bd6',
  primaryHover: '#6e6ee0',
  primarySoft: '#eef0ff',
  success: '#16a34a',
  warning: '#d97706',
  error: '#dc2626',
  text: '#1d2130',
  textSecondary: '#5c6170',
  textTertiary: '#8b90a0',
  border: '#e3e5eb',
  borderSoft: '#edeef2',
  bgLayout: '#f5f6fa',
  bgSubtle: '#f8f9fb',
};

const fontFamily =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", ' +
  '"Microsoft YaHei", Roboto, "Helvetica Neue", Arial, sans-serif';

const theme = {
  token: {
    colorPrimary: palette.primary,
    colorInfo: palette.primary,
    colorSuccess: palette.success,
    colorWarning: palette.warning,
    colorError: palette.error,
    colorLink: palette.primary,
    colorTextBase: palette.text,
    colorBorder: palette.border,
    colorBorderSecondary: palette.borderSoft,
    colorBgLayout: palette.bgLayout,
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    fontFamily,
    fontSize: 14,
    controlHeight: 34,
    boxShadow: '0 6px 24px rgba(17, 24, 39, 0.08), 0 1px 3px rgba(17, 24, 39, 0.06)',
    boxShadowSecondary: '0 10px 32px rgba(17, 24, 39, 0.12), 0 2px 6px rgba(17, 24, 39, 0.06)',
  },
  components: {
    Layout: {
      headerBg: '#ffffff',
      headerHeight: 56,
      headerPadding: '0 24px',
      siderBg: '#ffffff',
      bodyBg: palette.bgLayout,
    },
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: palette.textSecondary,
      itemHoverColor: palette.text,
      itemHoverBg: '#f2f3f7',
      itemSelectedBg: palette.primarySoft,
      itemSelectedColor: palette.primary,
      itemActiveBg: palette.primarySoft,
      itemBorderRadius: 8,
      itemHeight: 38,
      itemMarginInline: 10,
      itemMarginBlock: 2,
      iconSize: 16,
      collapsedIconSize: 17,
      groupTitleColor: palette.textTertiary,
      groupTitleFontSize: 11,
      groupTitleLineHeight: 1.5,
      activeBarBorderWidth: 0,
    },
    Card: {
      headerFontSize: 15,
      headerFontSizeSM: 14,
      headerHeight: 52,
      headerHeightSM: 44,
      paddingLG: 20,
    },
    Table: {
      headerBg: palette.bgSubtle,
      headerColor: palette.textSecondary,
      headerSplitColor: 'transparent',
      headerSortActiveBg: '#f1f2f6',
      headerSortHoverBg: '#f1f2f6',
      rowHoverBg: '#f7f8fc',
      borderColor: palette.borderSoft,
      cellPaddingBlock: 12,
      cellPaddingInline: 12,
      cellPaddingBlockMD: 10,
      cellPaddingInlineMD: 12,
      cellPaddingBlockSM: 8,
      cellPaddingInlineSM: 10,
      footerBg: palette.bgSubtle,
      fixedHeaderSortActiveBg: '#f1f2f6',
    },
    Modal: {
      titleFontSize: 16,
      headerBg: '#ffffff',
    },
    Drawer: {
      footerPaddingBlock: 12,
      footerPaddingInline: 24,
    },
    Button: {
      fontWeight: 500,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
    },
    Tag: {
      defaultBg: '#f3f4f7',
      defaultColor: palette.textSecondary,
    },
    Descriptions: {
      labelBg: palette.bgSubtle,
      labelColor: palette.textSecondary,
      titleMarginBottom: 12,
      itemPaddingBottom: 12,
      colonMarginRight: 6,
    },
    Statistic: {
      titleFontSize: 13,
      contentFontSize: 24,
    },
    Form: {
      labelColor: '#3f4354',
      verticalLabelPadding: '0 0 6px',
      itemMarginBottom: 18,
    },
    Tabs: {
      horizontalMargin: '0 0 16px 0',
      titleFontSize: 14,
    },
    Segmented: {
      itemSelectedColor: palette.text,
    },
    Alert: {
      withDescriptionPadding: '14px 16px',
    },
  },
};

export default theme;
